import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore, doc, getDocFromServer } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import firebaseConfig from '../../firebase-applet-config.json';

const app = initializeApp(firebaseConfig);

// CRITICAL: The app will break without this line
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId); // CRITICAL: The app will break without this line
export const dbDefault = getFirestore(app); // Standard (default) database instance where user data might reside
export const auth = getAuth(app);
export const storage = getStorage(app);

export enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
  }
}

export function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errMsg = error instanceof Error ? error.message : String(error);
  const isQuota = /quota|Quota|exhausted|limit|billing/i.test(errMsg);

  if (isQuota) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('firebase-quota-exceeded'));
    }
  }

  const errInfo: FirestoreErrorInfo = {
    error: errMsg,
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
    },
    operationType,
    path
  };

  if (isQuota) {
    console.warn('Firestore Database Quota Exceeded gracefully handled: ', JSON.stringify(errInfo));
    throw new Error('quota/quota-exceeded');
  }

  console.error('Firestore Error Details: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

// Validation check
async function testConnection() {
  try {
    const docRef = doc(db, 'test', 'connection');
    await getDocFromServer(docRef);
    console.log("Firebase connection successful");
  } catch (error) {
    if (error instanceof Error) {
      if (/quota|Quota|exhausted|limit|billing/i.test(error.message)) {
        console.warn("Firebase: Daily read quota limits hit. Activating readless fallback mode.");
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('firebase-quota-exceeded'));
        }
      } else if (error.message.includes('the client is offline')) {
        console.error("Firebase: The client is offline or the Firebase project is unreachable. Please check your internet connection or Firebase console status.");
      } else if (error.message.includes('permission-denied')) {
        console.warn("Firebase: Connection established but permission was denied for the test document. This is expected if rules are restrictive.");
      } else {
        console.error("Firebase connection error:", error.message);
      }
    } else {
      console.error("An unknown Firebase error occurred:", error);
    }
  }
}

testConnection();
